import React, { useEffect, useState } from 'react';
import { walletAPI } from '../../services/api';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaHome } from 'react-icons/fa';

export default function PaymentRequestsAdmin() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ 
    status: 'pending', 
    utr: '', 
    role: '' 
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await walletAPI.getAdminPaymentRequests(filters);
      // Handle paginated response - data is in res.data.data
      setItems(res.data?.data || res.data || []);
    } catch (error) {
      console.error('Error loading payment requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filters]);

  const act = async (id, action) => {
    const review_note = prompt(`${action.toUpperCase()} note (optional):`) || '';
    
    setActionLoading(id);
    try {
      await walletAPI.adminPaymentAction(id, { action, review_note });
      load(); // Reload the list
    } catch (error) {
      alert('Action failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-4">
      {/* Breadcrumb Navigation */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/admin/dashboard" className="text-decoration-none">
              <FaHome className="me-1" />
              Dashboard
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Payment Requests
          </li>
        </ol>
      </nav>

      {/* Header with Back Button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Link 
            to="/admin/dashboard" 
            className="btn btn-outline-secondary me-3"
            title="Back to Dashboard"
          >
            <FaArrowLeft className="me-1" />
            Back to Dashboard
          </Link>
          <h2 className="mb-0">Payment Requests (Admin)</h2>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <select 
          value={filters.status} 
          onChange={e => setFilters(f => ({...f, status: e.target.value}))} 
          className="border p-2 rounded"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        
        <input 
          placeholder="Filter by UTR" 
          className="border p-2 rounded" 
          value={filters.utr} 
          onChange={e => setFilters(f => ({...f, utr: e.target.value}))} 
        />
        
        <select 
          value={filters.role} 
          onChange={e => setFilters(f => ({...f, role: e.target.value}))} 
          className="border p-2 rounded"
        >
          <option value="">Any Role</option>
          <option value="user">User</option>
          <option value="distributor">Distributor</option>
        </select>
        
        <button 
          onClick={load} 
          className="border px-3 py-2 rounded hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-4">Loading...</div>
      ) : (
        <div className="space-y-3">
          {items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No payment requests found
            </div>
          ) : (
            items.map(r => (
              <div key={r.id} className="border p-4 rounded-lg shadow-sm">
                <div className="flex justify-between gap-4">
                  <div className="flex-1">
                    <div className="font-medium text-lg">
                      ₹{parseFloat(r.amount).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">
                      {new Date(r.transfer_at).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">
                      UTR: {r.utr_number}
                    </div>
                    <div className="text-sm text-gray-600">
                      By: {r.user?.name || r.user_id} ({r.requester_role})
                    </div>
                    {r.description && (
                      <div className="text-sm mt-1 text-gray-700">
                        {r.description}
                      </div>
                    )}
                  </div>
                  
                  <div className="text-right">
                    {r.slip_path && (
                      <a 
                        className="text-blue-600 underline text-sm block mb-2" 
                        href={`/api/admin/payment-requests/${r.id}/slip`} 
                        target="_blank" 
                        rel="noreferrer"
                      >
                        View Slip
                      </a>
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusBadge(r.status)}`}>
                      {r.status}
                    </span>
                  </div>
                </div>
                
                {r.status === 'pending' && (
                  <div className="flex gap-2 mt-3">
                    <button 
                      onClick={() => act(r.id, 'approve')} 
                      disabled={actionLoading === r.id}
                      className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {actionLoading === r.id ? 'Processing...' : 'Approve'}
                    </button>
                    <button 
                      onClick={() => act(r.id, 'reject')} 
                      disabled={actionLoading === r.id}
                      className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      {actionLoading === r.id ? 'Processing...' : 'Reject'}
                    </button>
                  </div>
                )}
                
                {r.status !== 'pending' && (
                  <div className="text-xs mt-2 p-2 bg-gray-50 rounded">
                    Reviewed {r.reviewed_at ? new Date(r.reviewed_at).toLocaleString() : ''} 
                    {r.review_note && ` — ${r.review_note}`}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
